import { useState, useEffect, useCallback } from 'react';
import {
  doc,
  getDoc,
  setDoc,
  onSnapshot,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../services/firebase/config';
import { useAuthBusiness } from '../contexts/AuthBusinessContext';

/**
 * NEW RESTAURANT SETTINGS HOOK
 * Yeni Firebase mimarisi için settings hook'u
 */
export const useNewRestaurantSettings = () => {
  const { selectedBusiness, currentUser } = useAuthBusiness();
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const restaurantId = selectedBusiness?.id;

  // Get current business ID for hibrit structure
  const getCurrentBusinessId = useCallback(() => {
    // console.log('🔍 selectedBusiness in hibrit hook:', selectedBusiness);
    let businessId = selectedBusiness?.id;

    // 🚀 QR Menu Exception: If no business selected, try to get from URL
    if (!businessId || businessId === 'business_account' || businessId === 'all') {
      const path = window.location.pathname;
      const pathMatch = path.match(/\/menu\/([^\/]+)/);
      if (pathMatch && pathMatch[1] !== 'all') {
        businessId = pathMatch[1];
      }
    }

    if (!businessId || businessId === 'business_account' || businessId === 'all') {
      // console.log('⚠️ Invalid business ID, returning null');
      return null;
    }

    // console.log('✅ Using hibrit business ID:', businessId);
    return businessId;
  }, [selectedBusiness]);

  // Get base path segments for settings (Ana işletme veya Şube izole)
  const getSettingsPathSegments = useCallback((settingType) => {
    const user = (() => {
      try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; }
    })();
    const isBranch = user.role === 'branch_manager' || user.is_branch_account;
    const branchId = localStorage.getItem('activeBranchId') || user.branch_id;
    const currentBusinessId = getCurrentBusinessId();
    const parentId = localStorage.getItem('parentBusinessId') || user.parent_business_id || currentBusinessId;

    const currentSector = selectedBusiness?.sector || selectedBusiness?.business_type || 'food_beverage';
    const normalizedSector = currentSector.replace(/-/g, '_');

    if (isBranch && branchId && parentId) {
      return ['businesses', normalizedSector, 'businesses', parentId, 'locations', branchId, 'settings', settingType];
    }
    return ['businesses', normalizedSector, 'businesses', currentBusinessId, 'settings', settingType];
  }, [getCurrentBusinessId, selectedBusiness]);

  // Load settings from hibrit Firebase structure
  const loadSettings = useCallback(async () => {
    const currentBusinessId = getCurrentBusinessId();
    if (!currentBusinessId) return;

    try {
      setLoading(true);
      setError(null);

      const currentSector = selectedBusiness?.sector || selectedBusiness?.business_type || 'food_beverage';
      const normalizedSector = currentSector.replace(/-/g, '_');

      // HIBRİT STRUCTURE: businesses/{sector}/businesses/{businessId}/settings/ (veya şube altı)
      const settingTypes = [
        'pos_settings',
        'kitchen_settings',
        'delivery_settings',
        'financial_settings',
        'platform_settings',
        'table_management',
        'printing',
        'notification_settings'
      ];

      const settingsData = {};

      await Promise.all(settingTypes.map(async (settingType) => {
        try {
          const pathSegments = getSettingsPathSegments(settingType);
          const settingRef = doc(db, ...pathSegments);
          const settingDoc = await getDoc(settingRef);

          if (settingDoc.exists()) {
            settingsData[settingType] = settingDoc.data();
          } else {
            // Create default (only for food_beverage to avoid poisoning other sectors)
            if (normalizedSector === 'food_beverage' || normalizedSector === 'restaurant') {
              const defaultData = getDefaultSettings(settingType);
              settingsData[settingType] = defaultData;

              // Yalnızca oturum açmış kullanıcı varsa Firestore'a varsayılan ayar kaydet, misafir ziyaretçide hata fırlatmasını önle
              if (currentUser) {
                setDoc(settingRef, {
                  ...defaultData,
                  business_id: currentBusinessId,
                  setting_type: settingType,
                  created_at: serverTimestamp(),
                  updated_at: serverTimestamp()
                }).catch(e => console.warn(`⚠️ Failed to create default ${settingType}:`, e));
              }
            } else {
              settingsData[settingType] = {};
            }
          }
        } catch (err) {
          console.warn(`⚠️ Could not load ${settingType}:`, err);
          settingsData[settingType] = {};
        }
      }));

      setSettings(settingsData);

    } catch (err) {
      console.error('Error loading settings:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [getCurrentBusinessId, getSettingsPathSegments, selectedBusiness]);

  // Save specific setting type to hibrit structure
  const saveSetting = useCallback(async (settingType, data) => {
    const currentBusinessId = getCurrentBusinessId();
    if (!currentBusinessId) return;

    try {
      setSaving(true);
      
      const pathSegments = getSettingsPathSegments(settingType);
      console.log(`🔄 Saving settings to: ${pathSegments.join('/')}`);

      const settingRef = doc(db, ...pathSegments);

      await setDoc(settingRef, {
        ...data,
        business_id: currentBusinessId,
        setting_type: settingType,
        updated_at: serverTimestamp()
      }, { merge: true });

      // Update local state
      setSettings(prev => ({
        ...prev,
        [settingType]: {
          ...data,
          business_id: currentBusinessId,
          setting_type: settingType
        }
      }));

      console.log(`✅ Saved hibrit ${settingType} successfully`);
      setError(null);

    } catch (err) {
      console.error(`Error saving ${settingType}:`, err);
      setError(err.message);
      throw err;
    } finally {
      setSaving(false);
    }
  }, [getCurrentBusinessId]);

  // Load settings on mount and restaurant change
  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  // POS Settings operations
  const updatePOSSettings = useCallback(async (posData) => {
    await saveSetting('pos_settings', posData);
  }, [saveSetting]);

  // Kitchen Settings operations  
  const updateKitchenSettings = useCallback(async (kitchenData) => {
    await saveSetting('kitchen_settings', kitchenData);
  }, [saveSetting]);

  // Delivery Settings operations
  const updateDeliverySettings = useCallback(async (deliveryData) => {
    await saveSetting('delivery_settings', deliveryData);
  }, [saveSetting]);

  // Financial Settings operations
  const updateFinancialSettings = useCallback(async (financialData) => {
    await saveSetting('financial_settings', financialData);
  }, [saveSetting]);

  // User Settings operations
  const updateUserSettings = useCallback(async (userData) => {
    await saveSetting('user_settings', userData);
  }, [saveSetting]);

  // Integrations Settings operations
  const updateIntegrationsSettings = useCallback(async (integrationsData) => {
    await saveSetting('integrations_settings', integrationsData);
  }, [saveSetting]);

  // Printer Settings operations
  const updatePrinterSettings = useCallback(async (printerData) => {
    await saveSetting('printing', printerData);
  }, [saveSetting]);

  // Notification Settings operations
  const updateNotificationSettings = useCallback(async (notificationData) => {
    await saveSetting('notification_settings', notificationData);
  }, [saveSetting]);

  return {
    // Data
    settings,

    // Status
    loading,
    saving,
    error,

    // Actions
    loadSettings,
    updatePOSSettings,
    updateKitchenSettings,
    updateDeliverySettings,
    updateFinancialSettings,
    updateUserSettings,
    updateIntegrationsSettings,
    updatePrinterSettings,
    updateNotificationSettings,

    // Helpers
    restaurantId: getCurrentBusinessId(),
    isReady: !loading && !error
  };
};

// Default settings generator
function getDefaultSettings(settingType) {
  switch (settingType) {
    case 'pos_settings':
      return {
        general: {
          defaultScreen: 'dashboard',
          autoLogout: true,
          autoLogoutTime: 30,
          confirmationRequired: true,
          soundEnabled: true
        },
        quickSales: {
          enabled: true,
          orderMethod: 'predefined_cards', // 'predefined_cards' | 'auto_numbering'

          // Hazır kart ayarları - BAŞLANGIÇTA BOŞ
          numberOfCards: 0,
          cards: [],

          // Otomatik numaralama ayarları  
          customerNumberPrefix: 'PKT',
          startingNumber: 1,
          dailyReset: true,
          resetTime: '00:00',
          currentNumber: 1,
          lastResetDate: null,

          // Genel ayarlar
          enableTimeTracking: true
        },
        tableService: { enabled: true, autoOpenTables: true },

        // Yeni Nesil ÖKC ve Ödeme Altyapısı
        paymentInfrastructure: {
          provider: 'none', // 'ingenico', 'beko', 'hugin', 'none'
          ipAddress: '192.168.1.100',
          port: 8080,
          terminalId: '',
          merchantId: '',
          serialNumber: '',
          departmentMappings: {
            food: 1, // Kısım 1
            beverage: 2, // Kısım 2
            other: 3 // Kısım 8
          },
          gmp3Enabled: false,
          autoZReport: false
        }
      };

    case 'kitchen_settings':
      return {
        general: {
          kitchenDisplayEnabled: true,
          autoRefreshInterval: 30
        },
        orderManagement: {
          autoAcceptOrders: false,
          maxPendingOrders: 20
        }
      };

    case 'delivery_settings':
      return {
        general: {
          enabled: true,
          minOrderAmount: 30,
          maxDeliveryDistance: 15
        },
        zones: []
      };

    case 'financial_settings':
      return {
        general: {
          currency: 'TRY',
          decimalPlaces: 2,
          taxIncluded: true,
          defaultTaxRate: 10,
          serviceFeeRate: 5,
          serviceFeeEnabled: false,
          roundingMethod: 'nearest',
          minimumAmount: 10
        },
        taxes: [
          { id: 1, name: 'KDV %10', rate: 10, isDefault: true, category: 'food' },
          { id: 2, name: 'KDV %20', rate: 20, isDefault: false, category: 'beverage' },
          { id: 3, name: 'KDV %1', rate: 1, isDefault: false, category: 'bread' }
        ],
        paymentMethods: {
          cash: { enabled: true, name: 'Nakit', fee: 0, minAmount: 0, maxAmount: 0, requireApproval: false },
          creditCard: { enabled: true, name: 'Kredi Kartı', fee: 2.5, minAmount: 10, maxAmount: 0, requireApproval: false },
          debitCard: { enabled: true, name: 'Banka Kartı', fee: 1.5, minAmount: 5, maxAmount: 0, requireApproval: false },
          mobilePayment: { enabled: false, name: 'Mobil Ödeme', fee: 1.8, minAmount: 1, maxAmount: 500, requireApproval: false },
          voucher: { enabled: true, name: 'Yemek Kartı', fee: 0, minAmount: 0, maxAmount: 0, requireApproval: false }
        },
        discounts: {
          enableDiscounts: true,
          maxDiscountPercent: 50,
          requireManagerApproval: true,
          discountReasons: ['Müşteri Memnuniyetsizliği', 'Personel İndirimi', 'Promosyon', 'Defolu Ürün', 'Geç Servis']
        },
        tips: {
          enabled: true,
          includedInBill: false,
          suggestedAmounts: [10, 15, 20],
          allowCustomAmount: true,
          distributionMethod: 'equal',
          cashTipHandling: 'daily',
          cardTipHandling: 'auto'
        },
        billing: {
          invoiceNumberPrefix: 'RES',
          invoiceNumberStart: 1000,
          resetInvoiceNumbers: 'yearly',
          printLogo: true,
          printAddress: true,
          printTaxInfo: true,
          printQRCode: false,
          footerText: 'Restoran adınız - İyi günler dileriz!',
          copyCount: 2
        },
        cashRegister: {
          autoZReport: false,
          zReportTime: '23:59',
          requireCashCount: true,
          allowNegativeBalance: false,
          startingCash: 100,
          maximumCash: 1000,
          printZReport: true,
          emailZReport: false,
          backupToCloud: true
        },
        accounting: {
          enabled: false,
          provider: 'none',
          autoSync: false,
          syncInterval: 'daily',
          syncItems: { sales: true, expenses: false, inventory: false, payroll: false }
        },
        costControl: {
          trackIngredientCosts: false,
          profitMarginTargets: { food: 60, beverage: 70, alcohol: 80 },
          costAlerts: { enabled: false, threshold: 5, frequency: 'daily' },
          inventoryValuation: 'fifo'
        },
        // Include the arrays by default
        customPaymentMethods: [],
        cashRegisters: []
      };

    case 'user_settings':
      return {
        users: [],
        roles: [
          { id: 'admin', name: 'Yönetici', permissions: ['all'] },
          { id: 'cashier', name: 'Kasiyer', permissions: ['pos', 'orders'] },
          { id: 'waiter', name: 'Garson', permissions: ['tables', 'orders'] }
        ],
        permissions: []
      };

    case 'table_management':
      return {
        auto_assign_tables: true,
        table_service_enabled: true,
        qr_ordering_enabled: false,
        default_table_capacity: 4
      };

    case 'printing':
      return {
        printers: [],
        templates: {}
      };

    case 'notification_settings':
      return {
        email: {
          enabled: true,
          useCustomSmtp: false,
          smtp: {
            host: '',
            port: 587,
            secure: false,
            username: '',
            password: '',
            fromEmail: '',
            fromName: ''
          },
          triggers: {
            order_received: true,
            preparing: false,
            ready: false,
            on_the_way: true,
            delivered: true,
            cancelled: true,
            quote_pending: true,
            quote_sent: true
          },
          templates: {
            order_received: {
              subject: 'Siparişiniz Alındı - {business_name}',
              body: 'Merhaba {customer_name},\n\n#{order_number} nolu siparişiniz başarıyla alındı.\n\nSipariş Takip: {tracking_url}\n\nTutar: {total_amount} TL'
            },
            preparing: {
              subject: 'Siparişiniz Hazırlanıyor - {business_name}',
              body: 'Merhaba {customer_name},\n\n#{order_number} nolu siparişiniz hazırlanıyor.\n\nSipariş Takip: {tracking_url}'
            },
            ready: {
              subject: 'Siparişiniz Hazır - {business_name}',
              body: 'Merhaba {customer_name},\n\n#{order_number} nolu siparişiniz hazır.\n\nSipariş Takip: {tracking_url}'
            },
            on_the_way: {
              subject: 'Siparişiniz Yola Çıktı! - {business_name}',
              body: 'Merhaba {customer_name},\n\n#{order_number} nolu siparişiniz yola çıktı! Kuryemiz {courier_name} size ulaştırmak üzere yolda.\n\nTakip: {tracking_url}'
            },
            delivered: {
              subject: 'Siparişiniz Teslim Edildi - {business_name}',
              body: 'Merhaba {customer_name},\n\n#{order_number} nolu siparişiniz teslim edildi. Afiyet olsun!\n\nYorum Yap / Değerlendir: {tracking_url}'
            },
            cancelled: {
              subject: 'Siparişiniz İptal Edildi - {business_name}',
              body: 'Merhaba {customer_name},\n\n#{order_number} nolu siparişiniz iptal edildi.\n\nİptal Nedeni: {cancel_reason}'
            },
            quote_pending: {
              subject: 'Fiyat Teklifi Talebiniz Alındı - {business_name}',
              body: 'Merhaba {customer_name},\n\n#{order_number} nolu özel siparişiniz için fiyat teklifi talebi başarıyla alınmıştır. En kısa sürede teklifimizi hazırlayıp size ileteceğiz.\n\nTakip: {tracking_url}'
            },
            quote_sent: {
              subject: 'Fiyat Teklifiniz Hazır! - {business_name}',
              body: 'Merhaba {customer_name},\n\n#{order_number} nolu özel siparişiniz için hazırladığımız fiyat teklifi hazırdır.\n\nTeklif Tutarı: {total_amount} TL\n\nTeklifi incelemek ve onaylamak için tıklayın: {tracking_url}'
            }
          }
        },
        push: {
          enabled: true,
          triggers: {
            order_received: true,
            preparing: true,
            ready: true,
            on_the_way: true,
            delivered: true,
            cancelled: true,
            quote_pending: true,
            quote_sent: true
          },
          templates: {
            order_received: {
              title: 'Siparişiniz Alındı! ✅',
              body: '#{order_number} nolu siparişiniz alındı ve sıraya eklendi.'
            },
            preparing: {
              title: 'Hazırlanıyor... 👨‍🍳',
              body: 'Siparişiniz hazırlanmaya başlandı.'
            },
            ready: {
              title: 'Siparişiniz Hazır! 🎉',
              body: 'Siparişiniz hazırlandı ve paketlendi.'
            },
            on_the_way: {
              title: 'Yolda! 🛵',
              body: 'Kuryemiz {courier_name} siparişinizi teslim etmek üzere yola çıktı!'
            },
            delivered: {
              title: 'Teslim Edildi! 🍽️',
              body: 'Siparişiniz teslim edildi. Afiyet olsun!'
            },
            cancelled: {
              title: 'İptal Edildi ❌',
              body: 'Siparişiniz iptal edildi.'
            },
            quote_pending: {
              title: 'Teklif Talebi Alındı 📋',
              body: 'Özel tasarım ürününüz için fiyat teklifi talebi alınmıştır.'
            },
            quote_sent: {
              title: 'Fiyat Teklifiniz Hazır! 💰',
              body: 'Özel siparişiniz için fiyat teklifi iletildi. Onaylamak için tıklayın.'
            }
          }
        },
        whatsapp: {
          enabled: true,
          triggers: {
            order_received: true,
            preparing: false,
            ready: false,
            on_the_way: true,
            delivered: true,
            cancelled: true,
            quote_pending: true,
            quote_sent: true
          },
          templates: {
            order_received: {
              body: 'Merhaba {customer_name}, #{order_number} nolu siparişiniz {business_name} tarafından başarıyla alındı. Tutar: {total_amount} TL. Sipariş takip linkiniz: {tracking_url}'
            },
            preparing: {
              body: 'Merhaba {customer_name}, #{order_number} nolu siparişiniz hazırlanıyor.'
            },
            ready: {
              body: 'Merhaba {customer_name}, #{order_number} nolu siparişiniz hazır.'
            },
            on_the_way: {
              body: 'Merhaba {customer_name}, #{order_number} nolu siparişiniz yola çıktı. Kurye {courier_name} size ulaştırmak üzere yolda. Anlık konum takibi: {tracking_url}'
            },
            delivered: {
              body: 'Merhaba {customer_name}, #{order_number} nolu siparişiniz teslim edilmiştir. Afiyet olsun!'
            },
            cancelled: {
              body: 'Merhaba {customer_name}, #{order_number} nolu siparişiniz iptal edilmiştir. İptal Nedeni: {cancel_reason}'
            },
            quote_pending: {
              body: 'Merhaba {customer_name}, #{order_number} nolu özel siparişiniz için fiyat teklifi talebiniz alınmıştır. Teklif hazırlandığında size bilgi vereceğiz.'
            },
            quote_sent: {
              body: 'Merhaba {customer_name}, #{order_number} nolu özel siparişiniz için fiyat teklifi hazırdır. Tutar: {total_amount} TL. İncelemek ve onaylamak için: {tracking_url}'
            }
          }
        }
      };

    default:
      return {};
  }
}

export default useNewRestaurantSettings;